import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertTriangle,
  ChevronDown,
  Car01,
  Check,
  Coins03,
  CurrencyDollarCircle,
  InfoCircle,
  LineChartUp01,
  Percent03,
  Scales02,
  TrendUp01,
  Users01,
} from "@untitledui/icons"

import { teamsApi } from "@/lib/api/teams"
import { useTeam, useTeamMembers } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { ApiError } from "@/lib/api/client"
import type {
  DealershipGoalsRewards,
  ServiceLaneBaseline,
  TeamSettingsMetadata,
} from "@/lib/api/types"
import { Roles } from "@/lib/team/roles"
import {
  BASELINE_DEFAULTS,
  formatMoney,
  formatMoneyExact,
  projectBaseline,
  suggestRateTriple,
  type RateSuggestion,
} from "@/lib/service-lane/baseline-math"
import { resolveActiveAdvisors } from "@/lib/dashboard/defaults"
import { cx } from "@/lib/utils/cx"

// Service Lane baseline configuration. These four numbers are the upstream
// truth that drives the suggested weekly/monthly goals, the $-denominated gap
// on the dashboard, and the projected gross profit shown in emails.
//
// Inputs are typed but the projections recalculate live as you tune them — so
// the ServiceManager + Samuel can have a "what does our target look like at
// 4% conversion vs. 6%" conversation right inside this screen.

type SectionKey = "inputs" | "projections" | "rates"

type FormState = {
  dailyLaneVolume: string
  visibleDamagePct: string
  avgRoValue: string
  targetConversionRate: string
  activeAdvisors: string
}

const EMPTY_FORM: FormState = {
  dailyLaneVolume: "",
  visibleDamagePct: "",
  avgRoValue: "",
  targetConversionRate: "",
  activeAdvisors: "",
}

function hydrateForm(b: ServiceLaneBaseline | undefined): FormState {
  return {
    dailyLaneVolume: b?.dailyLaneVolume != null ? String(b.dailyLaneVolume) : "",
    visibleDamagePct: b?.visibleDamagePct != null ? String(b.visibleDamagePct) : "",
    avgRoValue: b?.avgRoValue != null ? String(b.avgRoValue) : "",
    targetConversionRate:
      b?.targetConversionRate != null ? String(b.targetConversionRate) : "",
    activeAdvisors: b?.activeAdvisors != null ? String(b.activeAdvisors) : "",
  }
}

function parseNum(v: string): number | undefined {
  if (!v.trim()) return undefined
  const n = Number.parseFloat(v)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

function formToBaseline(form: FormState): ServiceLaneBaseline {
  return {
    dailyLaneVolume: parseNum(form.dailyLaneVolume),
    visibleDamagePct: parseNum(form.visibleDamagePct),
    avgRoValue: parseNum(form.avgRoValue),
    targetConversionRate: parseNum(form.targetConversionRate),
    activeAdvisors: parseNum(form.activeAdvisors),
  }
}

interface ServiceLaneSettingsProps {
  teamId: number | string | null | undefined
}

export function ServiceLaneSettings({ teamId }: ServiceLaneSettingsProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: team, isLoading } = useTeam(teamId)

  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const initialRef = useRef<FormState>(EMPTY_FORM)
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    inputs: true,
    projections: true,
    rates: true,
  })
  const [appliedTier, setAppliedTier] = useState<keyof ReturnType<typeof suggestRateTriple> | "custom" | null>(null)
  const [customPerEstimate, setCustomPerEstimate] = useState("")
  const [customPerPaid, setCustomPerPaid] = useState("")

  // Inferred advisor count from team membership — the "live" reality vs.
  // what the manager has typed into `activeAdvisors`. When the two
  // disagree we surface the gap so they can reconcile.
  const { data: members } = useTeamMembers(teamId ? Number(teamId) : null)
  const inferredAdvisorCount = useMemo(
    () => (members ?? []).filter((m) => m.role === Roles.ServiceAdvisor).length,
    [members],
  )

  useEffect(() => {
    if (!team) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata
    const next = hydrateForm(meta.serviceLane)
    setForm(next)
    initialRef.current = next
  }, [team])

  const liveBaseline = useMemo(() => formToBaseline(form), [form])
  const projection = useMemo(() => projectBaseline(liveBaseline), [liveBaseline])
  const effectiveAdvisorCount = useMemo(
    () => resolveActiveAdvisors(liveBaseline, inferredAdvisorCount),
    [liveBaseline, inferredAdvisorCount],
  )
  const rateTriple = useMemo(
    () => suggestRateTriple(liveBaseline, effectiveAdvisorCount),
    [liveBaseline, effectiveAdvisorCount],
  )

  // The four operational inputs feed the projection; `activeAdvisors` feeds
  // the rate-suggestion calculator. Both are tracked separately so the
  // "X of 4 set" summary still measures lane completeness, not pay-model
  // readiness.
  const filledCount = (form.dailyLaneVolume ? 1 : 0) +
    (form.visibleDamagePct ? 1 : 0) +
    (form.avgRoValue ? 1 : 0) +
    (form.targetConversionRate ? 1 : 0)

  // Currently-applied pay rates from team metadata. Lets the rate-tile
  // "Use these" button show a check-state when the manager has already
  // picked that tier, so re-opens read as "yes, that one's live."
  const currentPay = useMemo(() => {
    const gr = ((team?.metadata ?? {}) as TeamSettingsMetadata).goalsRewards
    const perEstimate = gr?.rewards?.perEstimateSubmitted?.monetary?.amount ?? 0
    const perPaid = gr?.rewards?.perEstimateCompleted?.monetary?.amount ?? 0
    return { perEstimate, perPaid }
  }, [team?.metadata])

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialRef.current),
    [form],
  )

  const advisorMismatch =
    parseNum(form.activeAdvisors) != null &&
    parseNum(form.activeAdvisors) !== inferredAdvisorCount &&
    inferredAdvisorCount > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamId || !team) return
    setSaving(true)
    try {
      await teamsApi.updateSettings(Number(teamId), {
        metadata: { serviceLane: formToBaseline(form) },
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      initialRef.current = form
      toast.success("Service lane baseline saved")
      const returnUrl = searchParams.get("return")
      if (returnUrl) navigate(returnUrl)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to save baseline"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  // Write a suggested rate pair into the team's `goalsRewards.rewards` and
  // toast confirmation. Persists immediately (independent of the baseline
  // save bar) so the manager sees the link between calculator → pay model
  // as a single tap, not a two-step save dance.
  const persistRates = async (
    tier: keyof ReturnType<typeof suggestRateTriple> | "custom",
    perEst: number,
    perPaid: number,
  ) => {
    if (!teamId || !team) return
    setSaving(true)
    try {
      const current = ((team.metadata ?? {}) as TeamSettingsMetadata).goalsRewards
      const next: DealershipGoalsRewards = {
        ...current,
        rewards: {
          ...(current?.rewards ?? {}),
          perEstimateSubmitted: {
            badge: current?.rewards?.perEstimateSubmitted?.badge ?? { enabled: false, label: "Estimate submitted" },
            monetary: { enabled: true, amount: perEst },
          },
          perEstimateCompleted: {
            badge: current?.rewards?.perEstimateCompleted?.badge ?? { enabled: false, label: "Estimate paid" },
            monetary: { enabled: true, amount: perPaid },
          },
        },
      }
      await teamsApi.updateSettings(Number(teamId), {
        metadata: { goalsRewards: next },
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      setAppliedTier(tier)
      toast.success(`Applied — $${perEst} per estimate, $${perPaid} per paid`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to apply rate"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const applyRate = (
    tier: keyof ReturnType<typeof suggestRateTriple>,
    s: RateSuggestion,
  ) => persistRates(tier, s.perEstimate, s.perPaid)

  const applyCustom = () => {
    const perEst = Math.max(0, Number.parseFloat(customPerEstimate) || 0)
    const perPaid = Math.max(0, Number.parseFloat(customPerPaid) || 0)
    if (perEst === 0 && perPaid === 0) {
      toast.error("Set at least one of the two rates before applying.")
      return
    }
    void persistRates("custom", perEst, perPaid)
  }

  if (!teamId) {
    return (
      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
        <p className="text-sm text-zinc-500">
          Select a dealership from the team switcher to configure the service lane.
        </p>
      </div>
    )
  }

  const fullyConfigured = filledCount === 4

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Top summary — colour-shifts with completion */}
      <div
        className={cx(
          "rounded-2xl border shadow-sm overflow-hidden",
          fullyConfigured
            ? "border-emerald-200/70 bg-emerald-50/40"
            : filledCount > 0
              ? "border-indigo-200/60 bg-indigo-50/30"
              : "border-amber-200/80 bg-amber-50/40",
        )}
      >
        <div className="px-6 py-5 flex items-start gap-4">
          <div
            className={cx(
              "size-11 rounded-2xl grid place-items-center shrink-0 shadow-md",
              fullyConfigured
                ? "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25"
                : filledCount > 0
                  ? "bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-indigo-600/25"
                  : "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25",
            )}
          >
            {fullyConfigured ? (
              <TrendUp01 className="size-5 text-white" aria-hidden />
            ) : (
              <AlertTriangle className="size-5 text-white" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold text-zinc-900">
              Service lane baseline
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">
              {fullyConfigured ? (
                <>
                  Configured. Your goal suggestions and $ projections downstream
                  are based on these numbers.
                </>
              ) : filledCount > 0 ? (
                <>
                  <span className="font-semibold text-zinc-900">
                    {filledCount} of 4
                  </span>{" "}
                  set. Unset fields fall back to industry defaults — fine to
                  start, but the more accurate the inputs, the more useful the
                  projections.
                </>
              ) : (
                <>
                  Set your operational baseline. These four numbers drive your
                  suggested goals and the $-denominated gap shown to managers
                  every week.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
          <p className="text-sm text-zinc-400">Loading…</p>
        </div>
      ) : (
        <div className="space-y-4 pb-28">
          <SectionCard
            icon={Scales02}
            tone="sky"
            title="Operational inputs"
            statusPill={
              <span
                className={cx(
                  "inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide",
                  fullyConfigured
                    ? "bg-emerald-100 text-emerald-800"
                    : filledCount > 0
                      ? "bg-zinc-100 text-zinc-600"
                      : "bg-amber-100 text-amber-800",
                )}
              >
                {filledCount} of 4 set
              </span>
            }
            preview="The four numbers downstream calculations depend on"
            isOpen={expanded.inputs}
            onToggle={() => setExpanded((e) => ({ ...e, inputs: !e.inputs }))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                id="lane-volume"
                label="Daily service lane volume"
                unit="cars / day"
                placeholder="e.g. 30"
                pattern="integer"
                value={form.dailyLaneVolume}
                onChange={(v) => setForm((f) => ({ ...f, dailyLaneVolume: v }))}
                helper="Average cars checked in per business day."
                icon={Car01}
                defaultHint={null}
              />
              <InputField
                id="damage-pct"
                label="Vehicles with visible damage"
                unit="%"
                placeholder={String(BASELINE_DEFAULTS.visibleDamagePct)}
                pattern="integer"
                value={form.visibleDamagePct}
                onChange={(v) => setForm((f) => ({ ...f, visibleDamagePct: v }))}
                helper="Of cars in your lane, the share with damage worth estimating."
                icon={Percent03}
                defaultHint={`Industry avg ~${BASELINE_DEFAULTS.visibleDamagePct}%`}
              />
              <InputField
                id="ro-value"
                label="Average repair order value"
                unit="$"
                unitPosition="prefix"
                placeholder={String(BASELINE_DEFAULTS.avgRoValue)}
                pattern="integer"
                value={form.avgRoValue}
                onChange={(v) => setForm((f) => ({ ...f, avgRoValue: v }))}
                helper="Typical gross profit per repair order at your body shop."
                icon={CurrencyDollarCircle}
                defaultHint={`Industry avg $4K–$8K`}
              />
              <InputField
                id="conversion-rate"
                label="Target estimate-to-close conversion"
                unit="%"
                placeholder={String(BASELINE_DEFAULTS.targetConversionRate)}
                pattern="decimal"
                value={form.targetConversionRate}
                onChange={(v) => setForm((f) => ({ ...f, targetConversionRate: v }))}
                helper="What percent of estimates you expect to book as ROs."
                icon={LineChartUp01}
                defaultHint={`Default ${BASELINE_DEFAULTS.targetConversionRate}%`}
              />
            </div>

            {/* Advisor count — separate row because it scopes the rate-
                suggestion calculator below, not the lane projection. */}
            <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50/40 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                <InputField
                  id="active-advisors"
                  label="Active service advisors"
                  unit="ppl"
                  placeholder={inferredAdvisorCount > 0 ? String(inferredAdvisorCount) : "e.g. 4"}
                  pattern="integer"
                  value={form.activeAdvisors}
                  onChange={(v) => setForm((f) => ({ ...f, activeAdvisors: v }))}
                  helper="How many advisors are writing estimates today. Drives the pay-rate calculator below."
                  icon={Users01}
                  defaultHint={
                    inferredAdvisorCount > 0
                      ? `${inferredAdvisorCount} on the team`
                      : null
                  }
                />
                <div className="space-y-1.5">
                  <p className="text-[13px] font-semibold text-zinc-700">From team membership</p>
                  <div
                    className={cx(
                      "rounded-xl border px-3 py-2.5 text-[13px]",
                      advisorMismatch
                        ? "border-amber-200 bg-amber-50/50 text-amber-900"
                        : "border-zinc-200 bg-white text-zinc-700",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Users01 className="size-4 text-zinc-400 shrink-0" aria-hidden />
                      <span className="font-semibold tabular-nums">{inferredAdvisorCount}</span>
                      <span className="text-zinc-500">currently on this dealership</span>
                    </div>
                    {advisorMismatch && (
                      <p className="text-[11.5px] mt-1.5 leading-snug">
                        Doesn't match your override —{" "}
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({ ...f, activeAdvisors: String(inferredAdvisorCount) }))
                          }
                          className="font-semibold underline underline-offset-2"
                        >
                          use {inferredAdvisorCount}
                        </button>{" "}
                        or keep your number if a hire is pending.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={TrendUp01}
            tone="emerald"
            title="Live projection"
            statusPill={
              projection.hasInputs ? (
                <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide">
                  {Math.round(projection.viableEstimatesPerWeek)} / wk viable
                </span>
              ) : (
                <span className="bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide">
                  Set lane volume first
                </span>
              )
            }
            preview={
              projection.hasInputs
                ? `Up to ${formatMoneyExact(projection.projectedWeeklyGrossProfit)}/wk · ${formatMoneyExact(projection.projectedMonthlyGrossProfit)}/mo at the ceiling`
                : "Projected viable estimates and dollar potential — appears once lane volume is set"
            }
            isOpen={expanded.projections}
            onToggle={() => setExpanded((e) => ({ ...e, projections: !e.projections }))}
          >
            {!projection.hasInputs ? (
              <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 p-5">
                <div className="flex items-start gap-3">
                  <InfoCircle className="size-4 text-zinc-400 mt-0.5 shrink-0" aria-hidden />
                  <p className="text-[13px] text-zinc-500 leading-relaxed">
                    Set <span className="font-semibold text-zinc-700">daily service lane volume</span> to
                    see live projections. The other fields fall back to industry
                    defaults until you tune them.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/60 via-white to-white p-5">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-2">
                    The math
                  </p>
                  <p className="text-[14.5px] text-zinc-900 leading-relaxed">
                    <span className="font-semibold">{projection.dailyLaneVolume}</span> cars/day ×{" "}
                    <span className="font-semibold">{projection.visibleDamagePct}%</span> damage ={" "}
                    <span className="font-semibold">
                      {Math.round(projection.viableEstimatesPerDay)}
                    </span>{" "}
                    viable estimates per day.
                  </p>
                  <p className="text-[14.5px] text-zinc-700 leading-relaxed mt-1">
                    At <span className="font-semibold">{projection.targetConversionRate}%</span>{" "}
                    conversion ×{" "}
                    <span className="font-semibold">
                      ${projection.avgRoValue.toLocaleString("en-US")}
                    </span>{" "}
                    avg RO, you're sitting on up to{" "}
                    <span className="font-semibold text-emerald-700">
                      {formatMoneyExact(projection.projectedWeeklyGrossProfit)}
                    </span>{" "}
                    in weekly gross profit potential.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ProjectionTile
                    label="Viable per week"
                    value={`${Math.round(projection.viableEstimatesPerWeek)}`}
                    sub="estimates if you write every damaged car"
                    valueClass="text-zinc-900"
                  />
                  <ProjectionTile
                    label="Viable per month"
                    value={`${Math.round(projection.viableEstimatesPerMonth)}`}
                    sub="≈ 4.3 weeks × weekly viable"
                    valueClass="text-zinc-900"
                  />
                  <ProjectionTile
                    label="Weekly $ potential"
                    value={formatMoney(projection.projectedWeeklyGrossProfit)}
                    sub="weekly viable × conversion × avg RO"
                    valueClass="text-emerald-700"
                  />
                  <ProjectionTile
                    label="Monthly $ potential"
                    value={formatMoney(projection.projectedMonthlyGrossProfit)}
                    sub="weekly potential × 4.33"
                    valueClass="text-emerald-700"
                  />
                </div>
                <p className="text-[12px] text-zinc-500 italic">
                  This is the ceiling. The actual target you negotiate with the
                  dealer lives in{" "}
                  <span className="font-semibold not-italic text-zinc-700">
                    Pay &amp; rewards
                  </span>
                  .
                </p>
              </div>
            )}
          </SectionCard>

          {/* Pay-rate suggestions — three logical tiers derived from the
              lane projection above. Apply writes directly into the
              dealership's Pay Model so the manager doesn't have to copy
              numbers across tabs. */}
          <SectionCard
            icon={Coins03}
            tone="amber"
            title="Pay rate suggestions"
            statusPill={
              <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide">
                3 tiers + custom
              </span>
            }
            preview="Anchored to your avg RO — pick a tier or set your own"
            isOpen={expanded.rates}
            onToggle={() => setExpanded((e) => ({ ...e, rates: !e.rates }))}
          >
            <div className="space-y-4">
              {/* Math derivation — makes the dependence on dealership
                  numbers visible. Updates live as the manager tunes the
                  baseline above. */}
              <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/40 px-4 py-3">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
                  Anchored to your numbers
                </p>
                <p className="text-[12.5px] text-zinc-700 leading-snug">
                  Your avg RO is{" "}
                  <span className="font-semibold tabular-nums">
                    ${projection.avgRoValue.toLocaleString("en-US")}
                  </span>{" "}
                  in gross profit. Tiers share{" "}
                  <span className="font-semibold">1% · 2.5% · 5%</span> of that
                  with the advisor when an estimate gets paid. Per-submitted is{" "}
                  <span className="font-semibold">~10%</span> of per-paid —
                  habit fuel, not the payoff.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <RateTile
                  tier="conservative"
                  label="Conservative"
                  s={rateTriple.conservative}
                  current={currentPay}
                  applied={appliedTier === "conservative"}
                  onApply={() => applyRate("conservative", rateTriple.conservative)}
                  saving={saving}
                />
                <RateTile
                  tier="standard"
                  label="Standard"
                  s={rateTriple.standard}
                  current={currentPay}
                  applied={appliedTier === "standard"}
                  onApply={() => applyRate("standard", rateTriple.standard)}
                  saving={saving}
                  highlighted
                />
                <RateTile
                  tier="generous"
                  label="Generous"
                  s={rateTriple.generous}
                  current={currentPay}
                  applied={appliedTier === "generous"}
                  onApply={() => applyRate("generous", rateTriple.generous)}
                  saving={saving}
                />
              </div>

              {/* Custom row — manager bypasses the tiers and sets their
                  own pair. Same persistence path; just two numeric inputs. */}
              <div className="rounded-xl border border-zinc-200 bg-white p-4">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">
                  Or set your own
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2.5">
                  <CustomRateInput
                    label="Per estimate"
                    placeholder={String(rateTriple.standard.perEstimate)}
                    value={customPerEstimate}
                    onChange={setCustomPerEstimate}
                  />
                  <CustomRateInput
                    label="Per paid"
                    placeholder={String(rateTriple.standard.perPaid)}
                    value={customPerPaid}
                    onChange={setCustomPerPaid}
                  />
                  <button
                    type="button"
                    onClick={applyCustom}
                    disabled={saving || appliedTier === "custom"}
                    className={cx(
                      "h-10 px-4 self-end rounded-lg text-[12.5px] font-semibold transition-colors duration-150 inline-flex items-center justify-center gap-1.5",
                      appliedTier === "custom"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                        : "bg-zinc-900 text-white hover:bg-black disabled:opacity-50",
                    )}
                  >
                    {appliedTier === "custom" ? (
                      <>
                        <Check className="size-3.5" aria-hidden />
                        Applied
                      </>
                    ) : (
                      "Apply"
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-400 mt-2 leading-snug">
                  Writes directly to Pay &amp; Rewards — leave a field empty
                  to set that rate to $0.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-[420px] z-30"
          >
            <div className="rounded-2xl border border-zinc-200/70 bg-white/95 backdrop-blur-xl shadow-2xl shadow-zinc-900/15 px-5 py-3 flex items-center gap-3">
              <div className="size-9 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                <Scales02 className="size-4 text-emerald-600" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-zinc-900">Unsaved changes</p>
                <p className="text-[11.5px] text-zinc-500 truncate">
                  Save to update every downstream goal and projection.
                </p>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="shrink-0 h-10 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold disabled:opacity-50 transition-colors duration-150 flex items-center gap-1.5"
              >
                {saving ? (
                  <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Save baseline"
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )
}

/* ───────────────────────────────────────────────── */

const TONE_TILE: Record<"emerald" | "indigo" | "amber" | "sky", string> = {
  emerald: "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25",
  indigo: "bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-indigo-600/25",
  amber: "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25",
  sky: "bg-gradient-to-br from-sky-500 to-sky-700 shadow-sky-600/25",
}

function SectionCard({
  icon: Icon,
  tone,
  title,
  statusPill,
  preview,
  isOpen,
  onToggle,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  tone: "emerald" | "indigo" | "amber" | "sky"
  title: string
  statusPill: React.ReactNode
  preview: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className={cx(
        "rounded-2xl border bg-white shadow-sm overflow-hidden",
        isOpen ? "border-zinc-300/70" : "border-zinc-200/60",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-zinc-50/60 transition-colors duration-100"
      >
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            TONE_TILE[tone],
          )}
        >
          <Icon className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[15px] font-semibold text-zinc-900">{title}</h4>
            {statusPill}
          </div>
          <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">{preview}</p>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="shrink-0 text-zinc-400"
        >
          <ChevronDown className="size-5" aria-hidden />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-5 pb-5 pt-2 border-t border-zinc-100">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function InputField({
  id,
  label,
  unit,
  unitPosition = "suffix",
  placeholder,
  pattern,
  value,
  onChange,
  helper,
  icon: Icon,
  defaultHint,
}: {
  id: string
  label: string
  unit: string
  unitPosition?: "prefix" | "suffix"
  placeholder: string
  pattern: "integer" | "decimal"
  value: string
  onChange: (v: string) => void
  helper: string
  icon: React.ComponentType<{ className?: string }>
  defaultHint: string | null
}) {
  const re = pattern === "integer" ? /^[0-9]*$/ : /^[0-9]*\.?[0-9]*$/
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={id}
          className="text-[13px] font-semibold text-zinc-700 inline-flex items-center gap-1.5"
        >
          <Icon className="size-3.5 text-zinc-400" aria-hidden />
          {label}
        </label>
        {defaultHint && (
          <span className="text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
            {defaultHint}
          </span>
        )}
      </div>
      <div className="relative">
        {unitPosition === "prefix" && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
            {unit}
          </span>
        )}
        <input
          id={id}
          type="text"
          inputMode={pattern === "integer" ? "numeric" : "decimal"}
          pattern={pattern === "integer" ? "[0-9]*" : "[0-9]*\\.?[0-9]*"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const v = e.target.value
            if (v === "" || re.test(v)) onChange(v)
          }}
          onFocus={(e) => e.currentTarget.select()}
          className={cx(
            "w-full h-11 rounded-xl border border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10",
            unitPosition === "prefix" ? "pl-7 pr-3" : "pl-3 pr-10",
          )}
        />
        {unitPosition === "suffix" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-[12px] font-semibold uppercase tracking-wide">
            {unit}
          </span>
        )}
      </div>
      <p className="text-[11.5px] text-zinc-500 leading-snug">{helper}</p>
    </div>
  )
}

function ProjectionTile({
  label,
  value,
  sub,
  valueClass,
}: {
  label: string
  value: string
  sub: string
  valueClass: string
}) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white px-4 py-3">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1">
        {label}
      </p>
      <p className={cx("text-[22px] font-bold tracking-tight leading-tight", valueClass)}>
        {value}
      </p>
      <p className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{sub}</p>
    </div>
  )
}

// Pay-rate tile. Three render in a row — conservative / standard / generous.
// The middle tile is highlighted as the recommended balance. Each tile
// shows the rate pair, the projected per-advisor monthly compensation at
// that tier, and the share of upside the dealership routes to the advisor
// pool. "Use these" applies the rates directly into Pay & Rewards.
// Single numeric input for the Custom rate row. Same styling family as
// InputField above but compact — used twice (per-estimate, per-paid)
// side-by-side with the Apply button.
function CustomRateInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
          $
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const v = e.target.value
            if (v === "" || /^[0-9]+$/.test(v)) onChange(v)
          }}
          onFocus={(e) => e.currentTarget.select()}
          className="w-full h-10 rounded-lg border border-zinc-200 bg-white pl-6 pr-3 text-[14px] text-zinc-900 placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
        />
      </div>
    </div>
  )
}

function RateTile({
  tier,
  label,
  s,
  current,
  applied,
  onApply,
  saving,
  highlighted = false,
}: {
  tier: "conservative" | "standard" | "generous"
  label: string
  s: RateSuggestion
  current: { perEstimate: number; perPaid: number }
  applied: boolean
  onApply: () => void
  saving: boolean
  highlighted?: boolean
}) {
  void tier
  const isCurrent =
    current.perEstimate === s.perEstimate && current.perPaid === s.perPaid
  const showCheck = applied || isCurrent
  return (
    <div
      className={cx(
        "relative rounded-xl border p-4 flex flex-col gap-3 transition-all duration-150",
        highlighted
          ? "border-amber-300 bg-gradient-to-br from-amber-50 via-white to-white shadow-md"
          : "border-zinc-200 bg-white shadow-sm",
        showCheck && "ring-2 ring-emerald-400/40",
      )}
    >
      {highlighted && (
        <span className="absolute -top-2 left-3 bg-amber-500 text-white text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
          Recommended
        </span>
      )}
      <p className="text-[12.5px] font-bold text-zinc-700 uppercase tracking-wide">
        {label}
      </p>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[28px] font-black text-zinc-900 leading-none tracking-tight tabular-nums">
          ${s.perPaid}
        </span>
        <span className="text-[12px] text-zinc-500 font-medium">per paid</span>
      </div>
      <div className="flex items-baseline gap-1.5 -mt-2">
        <span className="text-[14.5px] font-bold text-zinc-700 leading-none tabular-nums">
          + ${s.perEstimate}
        </span>
        <span className="text-[11.5px] text-zinc-500 font-medium">per submitted</span>
      </div>
      <div className="rounded-lg bg-zinc-50 px-3 py-2 space-y-1">
        <div className="flex items-center justify-between gap-2 text-[11.5px]">
          <span className="text-zinc-500">Advisor earns</span>
          <span className="font-bold text-emerald-700 tabular-nums">
            ~${s.advisorMonthly.toLocaleString("en-US")}/mo
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 text-[11.5px]">
          <span className="text-zinc-500">You share</span>
          <span className="font-bold text-zinc-700 tabular-nums">
            {Math.round(s.shareOfUpside * 100)}% of upside
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onApply}
        disabled={saving || showCheck}
        className={cx(
          "h-9 rounded-lg text-[12.5px] font-semibold transition-colors duration-150 flex items-center justify-center gap-1.5",
          showCheck
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
            : "bg-zinc-900 text-white hover:bg-black disabled:opacity-50",
        )}
      >
        {showCheck ? (
          <>
            <Check className="size-3.5" aria-hidden />
            Applied
          </>
        ) : (
          "Use these rates"
        )}
      </button>
    </div>
  )
}
